/**
 * MyMetricsCard · Driver App · Milestone B
 * ========================================
 * Mobile-friendly performance widget for the signed-in driver. Renders:
 *   • Today's hero numbers (profits / tips / score)
 *   • Pie of transaction-type distribution (Recharts)
 *   • Bar of payment-method counts
 *   • Quick PDF download for tax season
 *
 * Backed by:
 *   GET  /driver/metrics/me?from=&to=
 *   GET  /driver/metrics/export.pdf?from=&to=  (download)
 *
 * Real-time sensor pings are captured by the parent Dashboard's useTelematics
 * hook (separate file) which POSTs every 60s — this component just RENDERS
 * the latest score.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Activity, DollarSign, HandCoins, ShieldCheck, FileDown, RefreshCw, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";
import { toast } from "sonner";

import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

import api from "../../lib/api";

const POLL_MS = 30000;
const TX_COLORS = { cash: "#a1a1aa", card: "#60a5fa", prepaid: "#fbbf24" };

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function scoreClass(score) {
  if (score >= 90) return "text-emerald-300 border-emerald-400/40";
  if (score >= 75) return "text-amber-300 border-amber-400/40";
  return "text-red-300 border-red-400/40";
}

export default function MyMetricsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const mountedRef = useRef(true);
  const pollRef = useRef(null);

  const fetchMe = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = `?from=${todayISO()}&to=${todayISO()}&_=${Date.now()}`;
      const { data: d } = await api.get(`/driver/metrics/me${params}`, {
        headers: { "Cache-Control": "no-cache" },
      });
      if (mountedRef.current) setData(d);
    } catch (e) {
      if (mountedRef.current && !silent) toast.error("Could not load my metrics");
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const mounted = mountedRef;
    mounted.current = true;
    fetchMe(false);
    pollRef.current = setInterval(() => fetchMe(true), POLL_MS);
    return () => {
      mounted.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchMe]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/driver/metrics/export.pdf?from=${todayISO()}&to=${todayISO()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-metrics-${todayISO()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("PDF ready");
    } catch (e) {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const tx = data?.transaction_type || {};
  const pieData = [
    { name: "Cash", value: tx.cash || 0, color: TX_COLORS.cash },
    { name: "Card", value: tx.card || 0, color: TX_COLORS.card },
    { name: "Prepaid", value: tx.prepaid || 0, color: TX_COLORS.prepaid },
  ].filter((s) => s.value > 0);
  const totalTx = pieData.reduce((acc, s) => acc + s.value, 0);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="my-metrics-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-emerald-300"/>
          <h3 className="text-sm font-extrabold">My Metrics · Today</h3>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fetchMe(false)}
            disabled={loading}
            data-testid="my-metrics-refresh"
            className="text-zinc-400 h-7 px-2"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={downloadPdf}
            disabled={downloading}
            data-testid="my-metrics-pdf"
            className="text-amber-400 h-7 px-2"
            title="Download today's PDF"
          >
            {downloading ? <Loader2 className="h-3 w-3 animate-spin"/> : <FileDown className="h-3 w-3"/>}
          </Button>
        </div>
      </div>

      {/* Hero numbers · v2.7.0 batch DO+++++++ premium glass + glow */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="stat-card-base stat-card-tint-amber p-2.5 text-center group">
          <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold">Profits</div>
          <div className="hero-revenue-figure glow-amber text-base font-extrabold tabular-nums mt-1" data-testid="my-profits">{money(data?.profits_total)}</div>
          <div className="text-[9px] text-zinc-600 mt-0.5">{data?.trip_count ?? 0} trips</div>
        </div>
        <div className="stat-card-base stat-card-tint-mint p-2.5 text-center group">
          <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold">Tips</div>
          <div className="hero-revenue-figure glow-mint text-base font-extrabold tabular-nums mt-1" data-testid="my-tips">{money(data?.tips_total)}</div>
          <div className="text-[9px] text-zinc-600 mt-0.5">Gratuity</div>
        </div>
        <div className="stat-card-base stat-card-tint-sky p-2.5 text-center group">
          <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold">Score</div>
          <Badge variant="outline" className={`${scoreClass(data?.driving_score)} text-base font-extrabold tabular-nums px-2 mt-0.5`} data-testid="my-score">
            {data?.driving_score ?? "—"}
          </Badge>
          <div className="text-[9px] text-zinc-600 mt-0.5">
            {data?.driving_score_source === "telematics" ? "LIVE" : "baseline"}
          </div>
        </div>
      </div>

      {/* Charts row */}
      {totalTx > 0 ? (
        <div className="grid grid-cols-2 gap-3 mb-2" data-testid="my-metrics-charts">
          {/* Pie of transaction mix */}
          <div className="rounded border border-zinc-800 bg-zinc-950 p-2">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Tx Mix</div>
            <div style={{ width: "100%", height: 90 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={18} outerRadius={36} paddingAngle={3}>
                    {pieData.map((s) => <Cell key={`pie-${s.name}`} fill={s.color}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", fontSize: 11 }} itemStyle={{ color: "#e4e4e7" }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-2 flex-wrap mt-1">
              {pieData.map((s) => (
                <div key={s.name} className="flex items-center gap-1 text-[9px] text-zinc-400">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }}/>
                  {s.name} {s.value}
                </div>
              ))}
            </div>
          </div>

          {/* Bar of counts */}
          <div className="rounded border border-zinc-800 bg-zinc-950 p-2">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Trips by Method</div>
            <div style={{ width: "100%", height: 110 }}>
              <ResponsiveContainer>
                <BarChart data={pieData} margin={{ top: 5, right: 0, left: -25, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", fontSize: 11 }} itemStyle={{ color: "#e4e4e7" }} cursor={{ fill: "#27272a" }}/>
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {pieData.map((s) => <Cell key={`bar-${s.name}`} fill={s.color}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-zinc-500 text-xs py-6">
          No trips yet today — your first ride will populate the charts here.
        </div>
      )}
      <p className="text-[9px] text-zinc-600 text-center mt-1" data-testid="my-metrics-as-of">
        Updated · {data?.as_of ? new Date(data.as_of).toLocaleTimeString() : "—"} · auto-refresh 30s
      </p>
    </Card>
  );
}
